import React, { useEffect, useState } from 'react';
import { Card, Row, Col, Form, Button, Spinner } from 'react-bootstrap';
import axios from 'axios';
import toast from 'react-hot-toast';
import { API_BASE_URL, getBearerToken } from '../../../functions';
import { useNavigate, useParams } from 'react-router-dom';

const emptyHotel = { location: 'Makkah', hotel_name: '', category: '', distance: '', address: '' };
const emptyPrice = { room_type: 'double', price_adult: '', price_child: '', price_infant: '' };
const emptyFlight = {
  airline_id: '',
  flight_number: '',
  departure_airport: '',
  departure_date: '',
  departure_time: '',
  departure_terminal: '',
  arrival_airport: '',
  arrival_date: '',
  arrival_time: '',
  arrival_terminal: '',
  baggage_allowance: '',
  hand_carry: '',
  meals_included: false
};

const UmrahForm = () => {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [selectValue, setSelectValue] = useState(null);
  const [form, setForm] = useState({
    title: '',
    pnr: '',
    departure_direction: 'direct',
    return_direction: 'direct',
    non_refundable: false,
    non_changeable: false,
    zayarat_included: false,
    notes: '',
    discount_type: '',
    discount_adult: '',
    discount_child: '',
    discount_infant: '',
    total_seats: '',
  });
  const [hotels, setHotels] = useState([
    { ...emptyHotel, location: 'Makkah' },
    { ...emptyHotel, location: 'Madina' }
  ]);
  const [prices, setPrices] = useState([
    { room_type: 'double', price_adult: '', price_child: '', price_infant: '' },
    { room_type: 'triple', price_adult: '', price_child: '', price_infant: '' },
    { room_type: 'quad', price_adult: '', price_child: '', price_infant: '' },
    { room_type: 'sharing', price_adult: '', price_child: '', price_infant: '' },
  ]);
  const [departureFlights, setDepartureFlights] = useState([{ ...emptyFlight }]);
  const [returnFlights, setReturnFlights] = useState([{ ...emptyFlight }]);
  const navigate = useNavigate();

  useEffect(() => {
    // Load airlines and other select values
    axios.get(`${API_BASE_URL}/general`, {
      headers: { 'Authorization': `Bearer ${getBearerToken()}` }
    }).then(response => {
      setSelectValue(response?.data?.data);
    }).catch(error => {
      console.error('Error loading select values:', error);
    });

    // Load package data for editing
    if (isEdit) {
      axios.get(`${API_BASE_URL}/umrah-package/${id}`, {
        headers: { 'Authorization': `Bearer ${getBearerToken()}` }
      }).then(response => {
        const packageData = response.data.data;
        setForm({
          title: packageData.title || '',
          pnr: packageData.pnr || '',
          departure_direction: packageData.departure_direction || 'direct',
          return_direction: packageData.return_direction || 'direct',
          non_refundable: packageData.non_refundable || false,
          non_changeable: packageData.non_changeable || false,
          zayarat_included: packageData.zayarat_included || false,
          notes: packageData.notes || '',
          discount_type: packageData.discount_type || '',
          discount_adult: packageData.discount_adult || '',
          discount_child: packageData.discount_child || '',
          discount_infant: packageData.discount_infant || '',
          total_seats: packageData.total_seats || '',
        });

        // Load hotels
        if (packageData.hotels && packageData.hotels.length > 0) {
          setHotels(packageData.hotels.map(hotel => ({
            location: hotel.location,
            hotel_name: hotel.hotel_name,
            category: hotel.category || '',
            distance: hotel.distance || '',
            address: hotel.address || ''
          })));
        }

        // Load prices
        if (packageData.prices && packageData.prices.length > 0) {
          setPrices(packageData.prices.map(price => ({
            room_type: price.room_type,
            price_adult: price.price_adult || '',
            price_child: price.price_child || '',
            price_infant: price.price_infant || ''
          })));
        }

        // Load departure flights
        if (packageData.departure_flights && packageData.departure_flights.length > 0) {
          setDepartureFlights(packageData.departure_flights.map(flight => ({
            airline_id: flight.airline_id,
            flight_number: flight.flight_number || '',
            departure_airport: flight.departure_airport,
            departure_date: flight.departure_date ? flight.departure_date.split('T')[0] : '',
            departure_time: flight.departure_time,
            departure_terminal: flight.departure_terminal || '',
            arrival_airport: flight.arrival_airport,
            arrival_date: flight.arrival_date ? flight.arrival_date.split('T')[0] : '',
            arrival_time: flight.arrival_time,
            arrival_terminal: flight.arrival_terminal || '',
            baggage_allowance: flight.baggage_allowance || '',
            hand_carry: flight.hand_carry || '',
            meals_included: flight.meals_included || false
          })));
        }

        // Load return flights
        if (packageData.return_flights && packageData.return_flights.length > 0) {
          setReturnFlights(packageData.return_flights.map(flight => ({
            airline_id: flight.airline_id,
            flight_number: flight.flight_number || '',
            departure_airport: flight.departure_airport,
            departure_date: flight.departure_date ? flight.departure_date.split('T')[0] : '',
            departure_time: flight.departure_time,
            departure_terminal: flight.departure_terminal || '',
            arrival_airport: flight.arrival_airport,
            arrival_date: flight.arrival_date ? flight.arrival_date.split('T')[0] : '',
            arrival_time: flight.arrival_time,
            arrival_terminal: flight.arrival_terminal || '',
            baggage_allowance: flight.baggage_allowance || '',
            hand_carry: flight.hand_carry || '',
            meals_included: flight.meals_included || false
          })));
        }

        setLoading(false);
      }).catch(error => {
        console.error('Error loading package:', error);
        toast.error('Failed to load package data');
        setLoading(false);
      });
    }
  }, [id, isEdit]);

  const updateForm = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const updateHotel = (index, field, value) => {
    setHotels(prev => prev.map((hotel, i) => 
      i === index ? { ...hotel, [field]: value } : hotel
    ));
  };

  const updatePrice = (index, field, value) => {
    setPrices(prev => prev.map((price, i) => 
      i === index ? { ...price, [field]: value } : price
    ));
  };

  const updateDepartureFlight = (index, field, value) => {
    setDepartureFlights(prev => prev.map((flight, i) => 
      i === index ? { ...flight, [field]: value } : flight
    ));
  };

  const updateReturnFlight = (index, field, value) => {
    setReturnFlights(prev => prev.map((flight, i) => 
      i === index ? { ...flight, [field]: value } : flight
    ));
  };

  const addDepartureFlight = () => {
    if (departureFlights.length < 2) {
      setDepartureFlights(prev => [...prev, { ...emptyFlight }]);
    }
  };

  const removeDepartureFlight = (index) => {
    if (departureFlights.length > 1) {
      setDepartureFlights(prev => prev.filter((_, i) => i !== index));
    }
  };

  const addReturnFlight = () => {
    if (returnFlights.length < 2) {
      setReturnFlights(prev => [...prev, { ...emptyFlight }]);
    }
  };

  const removeReturnFlight = (index) => {
    if (returnFlights.length > 1) {
      setReturnFlights(prev => prev.filter((_, i) => i !== index));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const formData = new FormData();
      
      // Basic package data
      Object.keys(form).forEach(key => {
        formData.append(key, form[key]);
      });

      // Hotels
      console.log('Hotels data being sent:', hotels);
      hotels.forEach(hotel => {
        Object.keys(hotel).forEach(key => {
          formData.append(`hotels[${hotels.indexOf(hotel)}][${key}]`, hotel[key]);
        });
      });

      // Prices
      prices.forEach(price => {
        Object.keys(price).forEach(key => {
          formData.append(`prices[${prices.indexOf(price)}][${key}]`, price[key]);
        });
      });

      // Departure flights
      departureFlights.forEach(flight => {
        Object.keys(flight).forEach(key => {
          formData.append(`departure_flights[${departureFlights.indexOf(flight)}][${key}]`, flight[key]);
        });
      });

      // Return flights
      returnFlights.forEach(flight => {
        Object.keys(flight).forEach(key => {
          formData.append(`return_flights[${returnFlights.indexOf(flight)}][${key}]`, flight[key]);
        });
      });

      const endpoint = isEdit ? `update-umrah-package/${id}` : 'add-umrah-package';
      const response = await axios.post(`${API_BASE_URL}/${endpoint}`, formData, {
        headers: {
          'Authorization': `Bearer ${getBearerToken()}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.success) {
        toast.success(isEdit ? 'Package updated successfully!' : 'Package created successfully!');
        // Redirect based on user role
        const userRole = JSON.parse(localStorage.getItem('token'))?.role;
        if (userRole === 'admin') {
          navigate('/dashboard/umrah');
        } else {
          navigate('/dashboard/umrah-packages');
        }
      } else {
        toast.error(response.data.message || 'Something went wrong!');
      }
    } catch (error) {
      console.error('Error saving package:', error);
      toast.error(error.response?.data?.message || 'Failed to save package');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  return (
    <>
      <div className={'main_heading mb-3'}><h4>{isEdit ? 'Edit Umrah Package' : 'Add New Umrah Package'}</h4></div>
      <Form className={'AllForm'} method='post' onSubmit={handleSubmit}>
        {/* Basic Information */}
        <Card className={'mb-4'} style={{ border: '1px solid rgba(0, 0, 0, 0.2)', borderRadius: '16px' }}>
          <Card.Body className={'py-4'}>
            <h6 className='mb-3'>Basic Information</h6>
            <Row>
              <Col className={'mb-4'} xl={'6'} lg={'6'}>
                <Form.Group className='form-group'>
                  <Form.Label>Title *</Form.Label>
                  <Form.Control name='title' value={form.title} onChange={updateForm} required />
                </Form.Group>
              </Col>
              <Col className={'mb-4'} xl={'6'} lg={'6'}>
                <Form.Group className='form-group'>
                  <Form.Label>PNR</Form.Label>
                  <Form.Control name='pnr' value={form.pnr} onChange={updateForm} placeholder='Package PNR (Optional)' />
                </Form.Group>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Departure Flight Information */}
        <Card className={'mb-4'} style={{ border: '1px solid rgba(0, 0, 0, 0.2)', borderRadius: '16px' }}>
          <Card.Body className={'py-4'}>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h6>Departure Flight Information</h6>
              <Form.Group className="form-group mb-0">
                <Form.Select 
                  name='departure_direction' 
                  value={form.departure_direction} 
                  onChange={updateForm}
                  style={{ width: '150px' }}
                >
                  <option value='direct'>Direct</option>
                  <option value='via'>Via</option>
                </Form.Select>
              </Form.Group>
            </div>
            
            {departureFlights.map((flight, index) => (
              <div key={index} className="mb-3" style={{ border: '1px solid rgba(0, 0, 0, 0.1)', borderRadius: '8px', padding: '20px' }}>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h6 className="mb-0">Segment {index + 1}</h6>
                  {departureFlights.length > 1 && (
                    <Button 
                      variant="outline-danger" 
                      size="sm" 
                      onClick={() => removeDepartureFlight(index)}
                    >
                      Remove
                    </Button>
                  )}
                </div>
                <Row>
                    <Col className={'mb-3'} xl={'3'} lg={'3'}>
                      <Form.Group className='form-group'>
                        <Form.Label>Airline *</Form.Label>
                        <Form.Select 
                          name='airline_id' 
                          value={flight.airline_id} 
                          onChange={(e) => updateDepartureFlight(index, 'airline_id', e.target.value)} 
                          required
                          disabled={!selectValue}
                        >
                          <option value=''>{selectValue ? 'Select Airline' : 'Loading airlines...'}</option>
                          {selectValue?.airline?.map((item, i) => (
                            <option value={item?.id} key={item.id}>{item?.full_name}</option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col className={'mb-3'} xl={'3'} lg={'3'}>
                      <Form.Group className='form-group'>
                        <Form.Label>Flight Number *</Form.Label>
                        <Form.Control 
                          name='flight_number' 
                          value={flight.flight_number} 
                          onChange={(e) => updateDepartureFlight(index, 'flight_number', e.target.value)} 
                          placeholder='e.g., PK123'
                          required
                        />
                      </Form.Group>
                    </Col>
                    <Col className={'mb-3'} xl={'3'} lg={'3'}>
                      <Form.Group className='form-group'>
                        <Form.Label>Departure Airport *</Form.Label>
                        <Form.Control 
                          name='departure_airport' 
                          value={flight.departure_airport} 
                          onChange={(e) => updateDepartureFlight(index, 'departure_airport', e.target.value)} 
                          required
                        />
                      </Form.Group>
                    </Col>
                    <Col className={'mb-3'} xl={'3'} lg={'3'}>
                      <Form.Group className="form-group" controlId="exampleForm.ControlInput1">
                        <Form.Label>Departure Date *</Form.Label>
                        <Form.Control 
                          type={'date'} 
                          name='departure_date' 
                          value={flight.departure_date} 
                          onChange={(e) => updateDepartureFlight(index, 'departure_date', e.target.value)} 
                          required
                          placeholder="Date"
                          aria-label="Username"
                          aria-describedby="basic-addon1"
                        />
                      </Form.Group>
                    </Col>
                    <Col className={'mb-3'} xl={'3'} lg={'3'}>
                      <Form.Group className='form-group'>
                        <Form.Label>Departure Time *</Form.Label>
                        <Form.Control 
                          type='time' 
                          name='departure_time' 
                          value={flight.departure_time} 
                          onChange={(e) => updateDepartureFlight(index, 'departure_time', e.target.value)} 
                          required
                        />
                      </Form.Group>
                    </Col>
                    <Col className={'mb-3'} xl={'3'} lg={'3'}>
                      <Form.Group className='form-group'>
                        <Form.Label>Departure Terminal</Form.Label>
                        <Form.Control 
                          name='departure_terminal' 
                          value={flight.departure_terminal} 
                          onChange={(e) => updateDepartureFlight(index, 'departure_terminal', e.target.value)} 
                          placeholder='Optional'
                        />
                      </Form.Group>
                    </Col>
                    <Col className={'mb-3'} xl={'3'} lg={'3'}>
                      <Form.Group className='form-group'>
                        <Form.Label>Arrival Airport *</Form.Label>
                        <Form.Control 
                          name='arrival_airport' 
                          value={flight.arrival_airport} 
                          onChange={(e) => updateDepartureFlight(index, 'arrival_airport', e.target.value)} 
                          required
                        />
                      </Form.Group>
                    </Col>
                    <Col className={'mb-3'} xl={'3'} lg={'3'}>
                      <Form.Group className="form-group" controlId="exampleForm.ControlInput1">
                        <Form.Label>Arrival Date *</Form.Label>
                        <Form.Control 
                          type={'date'} 
                          name='arrival_date' 
                          value={flight.arrival_date} 
                          onChange={(e) => updateDepartureFlight(index, 'arrival_date', e.target.value)} 
                          required
                          placeholder="Date"
                          aria-label="Username"
                          aria-describedby="basic-addon1"
                        />
                      </Form.Group>
                    </Col>
                    <Col className={'mb-3'} xl={'3'} lg={'3'}>
                      <Form.Group className='form-group'>
                        <Form.Label>Arrival Time *</Form.Label>
                        <Form.Control 
                          type='time' 
                          name='arrival_time' 
                          value={flight.arrival_time} 
                          onChange={(e) => updateDepartureFlight(index, 'arrival_time', e.target.value)} 
                          required
                        />
                      </Form.Group>
                    </Col>
                    <Col className={'mb-3'} xl={'3'} lg={'3'}>
                      <Form.Group className='form-group'>
                        <Form.Label>Arrival Terminal</Form.Label>
                        <Form.Control 
                          name='arrival_terminal' 
                          value={flight.arrival_terminal} 
                          onChange={(e) => updateDepartureFlight(index, 'arrival_terminal', e.target.value)} 
                          placeholder='Optional'
                        />
                      </Form.Group>
                    </Col>
                    <Col className={'mb-3'} xl={'6'} lg={'6'}>
                      <Form.Group className='form-group'>
                        <Form.Label>Baggage Allowance *</Form.Label>
                        <Form.Control 
                          name='baggage_allowance' 
                          value={flight.baggage_allowance} 
                          onChange={(e) => updateDepartureFlight(index, 'baggage_allowance', e.target.value)} 
                          placeholder='e.g., 20kg'
                          required
                        />
                      </Form.Group>
                    </Col>
                    <Col className={'mb-3'} xl={'6'} lg={'6'}>
                      <Form.Group className='form-group'>
                        <Form.Label>Hand Carry *</Form.Label>
                        <Form.Control 
                          name='hand_carry' 
                          value={flight.hand_carry} 
                          onChange={(e) => updateDepartureFlight(index, 'hand_carry', e.target.value)} 
                          placeholder='e.g., 7kg'
                          required
                        />
                      </Form.Group>
                    </Col>
                    <Col className={'mb-3'} xl={'12'} lg={'12'}>
                      <Form.Group className='form-group'>
                        <Form.Check 
                          type="checkbox" 
                          id={`departure_meals_${index}`}
                          label="Meals Included" 
                          checked={flight.meals_included || false}
                          onChange={(e) => updateDepartureFlight(index, 'meals_included', e.target.checked)}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                </div>
            ))}
            
            {form.departure_direction === 'via' && departureFlights.length < 2 && (
              <Button variant="outline-primary" onClick={addDepartureFlight}>
                Add Another Segment
              </Button>
            )}
          </Card.Body>
        </Card>

        {/* Return Flight Information */}
        <Card className={'mb-4'} style={{ border: '1px solid rgba(0, 0, 0, 0.2)', borderRadius: '16px' }}>
          <Card.Body className={'py-4'}>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h6>Return Flight Information</h6>
              <Form.Group className="form-group mb-0">
                <Form.Select 
                  name='return_direction' 
                  value={form.return_direction} 
                  onChange={updateForm}
                  style={{ width: '150px' }}
                >
                  <option value='direct'>Direct</option>
                  <option value='via'>Via</option>
                </Form.Select>
              </Form.Group>
            </div>
            
            {returnFlights.map((flight, index) => (
              <div key={index} className="mb-3" style={{ border: '1px solid rgba(0, 0, 0, 0.1)', borderRadius: '8px', padding: '20px' }}>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h6 className="mb-0">Segment {index + 1}</h6>
                  {returnFlights.length > 1 && (
                    <Button 
                      variant="outline-danger" 
                      size="sm" 
                      onClick={() => removeReturnFlight(index)}
                    >
                      Remove
                    </Button>
                  )}
                </div>
                <Row>
                    <Col className={'mb-3'} xl={'3'} lg={'3'}>
                      <Form.Group className='form-group'>
                        <Form.Label>Airline *</Form.Label>
                        <Form.Select 
                          name='airline_id' 
                          value={flight.airline_id} 
                          onChange={(e) => updateReturnFlight(index, 'airline_id', e.target.value)} 
                          required
                          disabled={!selectValue}
                        >
                          <option value=''>{selectValue ? 'Select Airline' : 'Loading airlines...'}</option>
                          {selectValue?.airline?.map((item, i) => (
                            <option value={item?.id} key={item.id}>{item?.full_name}</option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col className={'mb-3'} xl={'3'} lg={'3'}>
                      <Form.Group className='form-group'>
                        <Form.Label>Flight Number *</Form.Label>
                        <Form.Control 
                          name='flight_number' 
                          value={flight.flight_number} 
                          onChange={(e) => updateReturnFlight(index, 'flight_number', e.target.value)} 
                          placeholder='e.g., PK123'
                          required
                        />
                      </Form.Group>
                    </Col>
                    <Col className={'mb-3'} xl={'3'} lg={'3'}>
                      <Form.Group className='form-group'>
                        <Form.Label>Departure Airport *</Form.Label>
                        <Form.Control 
                          name='departure_airport' 
                          value={flight.departure_airport} 
                          onChange={(e) => updateReturnFlight(index, 'departure_airport', e.target.value)} 
                          required
                        />
                      </Form.Group>
                    </Col>
                    <Col className={'mb-3'} xl={'3'} lg={'3'}>
                      <Form.Group className="form-group" controlId="exampleForm.ControlInput1">
                        <Form.Label>Departure Date *</Form.Label>
                        <Form.Control 
                          type={'date'} 
                          name='departure_date' 
                          value={flight.departure_date} 
                          onChange={(e) => updateReturnFlight(index, 'departure_date', e.target.value)} 
                          required
                          placeholder="Date"
                          aria-label="Username"
                          aria-describedby="basic-addon1"
                        />
                      </Form.Group>
                    </Col>
                    <Col className={'mb-3'} xl={'3'} lg={'3'}>
                      <Form.Group className='form-group'>
                        <Form.Label>Departure Time *</Form.Label>
                        <Form.Control 
                          type='time' 
                          name='departure_time' 
                          value={flight.departure_time} 
                          onChange={(e) => updateReturnFlight(index, 'departure_time', e.target.value)} 
                          required
                        />
                      </Form.Group>
                    </Col>
                    <Col className={'mb-3'} xl={'3'} lg={'3'}>
                      <Form.Group className='form-group'>
                        <Form.Label>Departure Terminal</Form.Label>
                        <Form.Control 
                          name='departure_terminal' 
                          value={flight.departure_terminal} 
                          onChange={(e) => updateReturnFlight(index, 'departure_terminal', e.target.value)} 
                          placeholder='Optional'
                        />
                      </Form.Group>
                    </Col>
                    <Col className={'mb-3'} xl={'3'} lg={'3'}>
                      <Form.Group className='form-group'>
                        <Form.Label>Arrival Airport *</Form.Label>
                        <Form.Control 
                          name='arrival_airport' 
                          value={flight.arrival_airport} 
                          onChange={(e) => updateReturnFlight(index, 'arrival_airport', e.target.value)} 
                          required
                        />
                      </Form.Group>
                    </Col>
                    <Col className={'mb-3'} xl={'3'} lg={'3'}>
                      <Form.Group className="form-group" controlId="exampleForm.ControlInput1">
                        <Form.Label>Arrival Date *</Form.Label>
                        <Form.Control 
                          type={'date'} 
                          name='arrival_date' 
                          value={flight.arrival_date} 
                          onChange={(e) => updateReturnFlight(index, 'arrival_date', e.target.value)} 
                          required
                          placeholder="Date"
                          aria-label="Username"
                          aria-describedby="basic-addon1"
                        />
                      </Form.Group>
                    </Col>
                    <Col className={'mb-3'} xl={'3'} lg={'3'}>
                      <Form.Group className='form-group'>
                        <Form.Label>Arrival Time *</Form.Label>
                        <Form.Control 
                          type='time' 
                          name='arrival_time' 
                          value={flight.arrival_time} 
                          onChange={(e) => updateReturnFlight(index, 'arrival_time', e.target.value)} 
                          required
                        />
                      </Form.Group>
                    </Col>
                    <Col className={'mb-3'} xl={'3'} lg={'3'}>
                      <Form.Group className='form-group'>
                        <Form.Label>Arrival Terminal</Form.Label>
                        <Form.Control 
                          name='arrival_terminal' 
                          value={flight.arrival_terminal} 
                          onChange={(e) => updateReturnFlight(index, 'arrival_terminal', e.target.value)} 
                          placeholder='Optional'
                        />
                      </Form.Group>
                    </Col>
                    <Col className={'mb-3'} xl={'6'} lg={'6'}>
                      <Form.Group className='form-group'>
                        <Form.Label>Baggage Allowance *</Form.Label>
                        <Form.Control 
                          name='baggage_allowance' 
                          value={flight.baggage_allowance} 
                          onChange={(e) => updateReturnFlight(index, 'baggage_allowance', e.target.value)} 
                          placeholder='e.g., 20kg'
                          required
                        />
                      </Form.Group>
                    </Col>
                    <Col className={'mb-3'} xl={'6'} lg={'6'}>
                      <Form.Group className='form-group'>
                        <Form.Label>Hand Carry *</Form.Label>
                        <Form.Control 
                          name='hand_carry' 
                          value={flight.hand_carry} 
                          onChange={(e) => updateReturnFlight(index, 'hand_carry', e.target.value)} 
                          placeholder='e.g., 7kg'
                          required
                        />
                      </Form.Group>
                    </Col>
                    <Col className={'mb-3'} xl={'12'} lg={'12'}>
                      <Form.Group className='form-group'>
                        <Form.Check 
                          type="checkbox" 
                          id={`return_meals_${index}`}
                          label="Meals Included" 
                          checked={flight.meals_included || false}
                          onChange={(e) => updateReturnFlight(index, 'meals_included', e.target.checked)}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                </div>
            ))}
            
            {form.return_direction === 'via' && returnFlights.length < 2 && (
              <Button variant="outline-primary" onClick={addReturnFlight}>
                Add Another Segment
              </Button>
            )}
          </Card.Body>
        </Card>

        {/* Hotel Information */}
        <Card className={'mb-4'} style={{ border: '1px solid rgba(0, 0, 0, 0.2)', borderRadius: '16px' }}>
          <Card.Body className={'py-4'}>
            <h6 className='mb-3'>Hotel Information</h6>
            {hotels.map((hotel, index) => (
              <Card key={index} className="mb-3" style={{ border: '1px solid rgba(0, 0, 0, 0.1)', borderRadius: '8px' }}>
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h6 className="mb-0">{hotel.location} Hotel</h6>
                    <Form.Group className="form-group mb-0">
                      <Form.Select 
                        name='location' 
                        value={hotel.location} 
                        onChange={(e) => updateHotel(index, 'location', e.target.value)}
                        style={{ width: '120px' }}
                      >
                        <option value='Makkah'>Makkah</option>
                        <option value='Madina'>Madina</option>
                      </Form.Select>
                    </Form.Group>
                  </div>
                  <Row>
                    <Col className={'mb-3'} xl={'6'} lg={'6'}>
                      <Form.Group className='form-group'>
                        <Form.Label>Hotel Name *</Form.Label>
                        <Form.Control 
                          name='hotel_name' 
                          value={hotel.hotel_name} 
                          onChange={(e) => updateHotel(index, 'hotel_name', e.target.value)} 
                          required
                        />
                      </Form.Group>
                    </Col>
                    <Col className={'mb-3'} xl={'6'} lg={'6'}>
                      <Form.Group className='form-group'>
                        <Form.Label>Category *</Form.Label>
                        <Form.Control 
                          name='category' 
                          value={hotel.category} 
                          onChange={(e) => updateHotel(index, 'category', e.target.value)} 
                          placeholder='e.g., Economy, Standard, Deluxe, Luxury'
                          required
                        />
                      </Form.Group>
                    </Col>
                    <Col className={'mb-3'} xl={'6'} lg={'6'}>
                      <Form.Group className='form-group'>
                        <Form.Label>Distance from Haram *</Form.Label>
                        <Form.Control 
                          name='distance' 
                          value={hotel.distance} 
                          onChange={(e) => updateHotel(index, 'distance', e.target.value)} 
                          placeholder='e.g., 600-700m'
                          required
                        />
                      </Form.Group>
                    </Col>
                    <Col className={'mb-3'} xl={'6'} lg={'6'}>
                      <Form.Group className='form-group'>
                        <Form.Label>Address *</Form.Label>
                        <Form.Control 
                          name='address' 
                          value={hotel.address} 
                          onChange={(e) => updateHotel(index, 'address', e.target.value)} 
                          placeholder='Hotel address'
                          required
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            ))}
          </Card.Body>
        </Card>

        {/* Package Features */}
        <Card className={'mb-4'} style={{ border: '1px solid rgba(0, 0, 0, 0.2)', borderRadius: '16px' }}>
          <Card.Body className={'py-4'}>
            <h6 className='mb-3'>Package Features</h6>
            <Row>
              <Col className={'mb-4'} xl={'3'} lg={'3'}>
                <Form.Group className='form-group'>
                  <Form.Check 
                    type="checkbox" 
                    id="non_refundable"
                    name="non_refundable"
                    label="Non-Refundable" 
                    checked={form.non_refundable}
                    onChange={updateForm}
                  />
                </Form.Group>
              </Col>
              <Col className={'mb-4'} xl={'3'} lg={'3'}>
                <Form.Group className='form-group'>
                  <Form.Check 
                    type="checkbox" 
                    id="non_changeable"
                    name="non_changeable"
                    label="Non-Changeable" 
                    checked={form.non_changeable}
                    onChange={updateForm}
                  />
                </Form.Group>
              </Col>
              <Col className={'mb-4'} xl={'3'} lg={'3'}>
                <Form.Group className='form-group'>
                  <Form.Check 
                    type="checkbox" 
                    id="zayarat_included"
                    name="zayarat_included"
                    label="Zayarat Included" 
                    checked={form.zayarat_included}
                    onChange={updateForm}
                  />
                </Form.Group>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Pricing & Seats */}
        <Card className={'mb-4'} style={{ border: '1px solid rgba(0, 0, 0, 0.2)', borderRadius: '16px' }}>
          <Card.Body className={'py-4'}>
            <h6 className='mb-3'>Pricing & Seats</h6>
            <Row>
              <Col className={'mb-4'} xl={'6'} lg={'6'}>
                <Form.Group className='form-group'>
                  <Form.Label>Total Seats *</Form.Label>
                  <Form.Control 
                    type='number' 
                    name='total_seats' 
                    value={form.total_seats} 
                    onChange={updateForm} 
                    required
                    min="1"
                  />
                </Form.Group>
              </Col>
              <Col className={'mb-4'} xl={'12'} lg={'12'}>
                <Form.Group className='form-group'>
                  <Form.Label>Discount Type</Form.Label>
                  <Form.Select name='discount_type' value={form.discount_type} onChange={updateForm}>
                    <option value=''>No Discount</option>
                    <option value='percentage'>Percentage</option>
                    <option value='fixed'>Fixed Amount</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              {form.discount_type && (
                <>
                  <Col className={'mb-4'} xl={'4'} lg={'4'}>
                    <Form.Group className='form-group'>
                      <Form.Label>
                        Adult Discount {form.discount_type === 'percentage' ? '(%)' : '(PKR)'}
                      </Form.Label>
                      <Form.Control 
                        type='number' 
                        name='discount_adult' 
                        value={form.discount_adult} 
                        onChange={updateForm} 
                        min="0"
                        max={form.discount_type === 'percentage' ? "100" : undefined}
                        placeholder={form.discount_type === 'percentage' ? 'e.g., 10' : 'e.g., 5000'}
                      />
                    </Form.Group>
                  </Col>
                  <Col className={'mb-4'} xl={'4'} lg={'4'}>
                    <Form.Group className='form-group'>
                      <Form.Label>
                        Child Discount {form.discount_type === 'percentage' ? '(%)' : '(PKR)'}
                      </Form.Label>
                      <Form.Control 
                        type='number' 
                        name='discount_child' 
                        value={form.discount_child} 
                        onChange={updateForm} 
                        min="0"
                        max={form.discount_type === 'percentage' ? "100" : undefined}
                        placeholder={form.discount_type === 'percentage' ? 'e.g., 10' : 'e.g., 5000'}
                      />
                    </Form.Group>
                  </Col>
                  <Col className={'mb-4'} xl={'4'} lg={'4'}>
                    <Form.Group className='form-group'>
                      <Form.Label>
                        Infant Discount {form.discount_type === 'percentage' ? '(%)' : '(PKR)'}
                      </Form.Label>
                      <Form.Control 
                        type='number' 
                        name='discount_infant' 
                        value={form.discount_infant} 
                        onChange={updateForm} 
                        min="0"
                        max={form.discount_type === 'percentage' ? "100" : undefined}
                        placeholder={form.discount_type === 'percentage' ? 'e.g., 10' : 'e.g., 5000'}
                      />
                    </Form.Group>
                  </Col>
                </>
              )}
            </Row>
            
            <h6 className='mb-3 mt-4'>Room Type Prices</h6>
            {prices.map((price, index) => (
              <Card key={index} className="mb-3" style={{ border: '1px solid rgba(0, 0, 0, 0.1)', borderRadius: '8px' }}>
                <Card.Body>
                  <Row>
                    <Col className={'mb-3'} xl={'3'} lg={'3'}>
                      <Form.Group className='form-group'>
                        <Form.Label>Room Type</Form.Label>
                        <Form.Control 
                          name='room_type' 
                          value={price.room_type === "sharing" ? "quint/sharing" : price.room_type} 
                          readOnly
                          style={{ backgroundColor: '#f8f9fa' }}
                        />
                      </Form.Group>
                    </Col>
                    <Col className={'mb-3'} xl={'3'} lg={'3'}>
                      <Form.Group className='form-group'>
                        <Form.Label>Adult Price (PKR) *</Form.Label>
                        <Form.Control 
                          type='number' 
                          name='price_adult' 
                          value={price.price_adult} 
                          onChange={(e) => updatePrice(index, 'price_adult', e.target.value)} 
                          min="0"
                          required
                        />
                      </Form.Group>
                    </Col>
                    <Col className={'mb-3'} xl={'3'} lg={'3'}>
                      <Form.Group className='form-group'>
                        <Form.Label>Child Price (PKR) *</Form.Label>
                        <Form.Control 
                          type='number' 
                          name='price_child' 
                          value={price.price_child} 
                          onChange={(e) => updatePrice(index, 'price_child', e.target.value)} 
                          min="0"
                          required
                        />
                      </Form.Group>
                    </Col>
                    <Col className={'mb-3'} xl={'3'} lg={'3'}>
                      <Form.Group className='form-group'>
                        <Form.Label>Infant Price (PKR) *</Form.Label>
                        <Form.Control 
                          type='number' 
                          name='price_infant' 
                          value={price.price_infant} 
                          onChange={(e) => updatePrice(index, 'price_infant', e.target.value)} 
                          min="0"
                          required
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            ))}
          </Card.Body>
        </Card>

        {/* Notes */}
        <Card className={'mb-4'} style={{ border: '1px solid rgba(0, 0, 0, 0.2)', borderRadius: '16px' }}>
          <Card.Body className={'py-4'}>
            <h6 className='mb-3'>Additional Notes</h6>
            <Row>
              <Col className={'mb-4'} xl={'12'} lg={'12'}>
                <Form.Group className='form-group'>
                  <Form.Label>Notes</Form.Label>
                  <Form.Control 
                    as='textarea' 
                    rows={3} 
                    name='notes' 
                    value={form.notes} 
                    onChange={updateForm} 
                    placeholder='Enter any additional notes... (Optional)'
                  />
                </Form.Group>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Submit Button */}
        <div className="d-flex justify-content-end">
          <Button 
            type="submit" 
            variant="success" 
            size="lg" 
            disabled={saving}
            style={{ minWidth: '150px' }}
          >
            {saving ? (
              <>
                <Spinner as="span" animation="border" size="sm" className="me-2" />
                {isEdit ? 'Updating...' : 'Creating...'}
              </>
            ) : (
              isEdit ? 'Update Package' : 'Create Package'
            )}
          </Button>
        </div>
      </Form>
    </>
  );
};

export default UmrahForm;